import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult, type PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './styles.js';
import type { MenuItem, MenuItemCommandEvent, MenuItemSelectEventDetail, Placement } from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController } from '../utils/placement.controller.js';

export type SplitButtonVariant = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'help' | 'danger' | 'contrast';
export type SplitButtonSize    = 'small' | 'medium' | 'large';
export type SplitButtonIconPos = 'left' | 'right';

/**
 * uwc-splitbutton
 *
 * A split button that combines a primary action button with a dropdown menu
 * trigger. The main button fires `uwc-click`; the chevron opens a panel of
 * secondary actions driven by the `items` MenuItem model. Supports all button
 * variants, sizes, and style modifiers. The dropdown panel is positioned via
 * `PlacementController` with the Popover API.
 *
 * @attr {string}   label        - Label on the primary action button.
 * @attr {string}   icon         - Icon name for the primary button.
 * @attr {string}   icon-pos     - Icon position: `'left'` | `'right'`. Default: `'left'`.
 * @attr {string}   variant      - Visual variant. Default: `'primary'`.
 * @attr {string}   size         - `'small'` | `'medium'` | `'large'`. Default: `'medium'`.
 * @attr {boolean}  raised       - Adds an elevation shadow to both buttons.
 * @attr {boolean}  rounded      - Pill-shaped outer corners.
 * @attr {boolean}  outlined     - Border-only style (no fill).
 * @attr {boolean}  text         - Ghost / text-only style.
 * @attr {boolean}  disabled     - Disables the entire component.
 * @attr {boolean}  loading      - Shows a spinner on the primary button and blocks clicks.
 * @attr {string}   loading-icon - Custom icon name for the loading spinner. Default: `'arrow-clockwise'`.
 * @attr {boolean}  fluid        - Stretches the component to fill its container width.
 * @attr {string}   placement    - Dropdown placement relative to the chevron button. Default: `'bottom-end'`.
 * @attr {number}   offset       - Gap in pixels between the chevron button and the panel. Default: `4`.
 *
 * @prop {MenuItem[]} items - The dropdown menu items.
 *
 * @slot default - Content for the primary button when `label` is not set.
 *
 * @fires uwc-click       - Primary button was clicked. Detail: `{ originalEvent }`.
 * @fires uwc-show        - Dropdown panel was opened.
 * @fires uwc-hide        - Dropdown panel was closed.
 * @fires uwc-item-select - A dropdown item was activated. Detail: `{ item, originalEvent }`.
 *
 * @csspart main-btn    - The primary action `<button>`.
 * @csspart trigger-btn - The chevron dropdown trigger `<button>`.
 * @csspart panel       - The dropdown `<ul>` panel.
 *
 * @cssprop --uwc-spb-divider           - Separator color between the two buttons. Default: semi-transparent text color.
 * @cssprop --uwc-spb-panel-bg          - Panel background. Default: surface token.
 * @cssprop --uwc-spb-panel-border      - Panel border. Default: border token.
 * @cssprop --uwc-spb-panel-radius      - Panel border-radius. Default: radius-md token.
 * @cssprop --uwc-spb-panel-shadow      - Panel box-shadow. Default: shadow-lg token.
 * @cssprop --uwc-spb-panel-padding     - Panel block padding. Default: `0.25rem 0`.
 * @cssprop --uwc-spb-panel-min-width   - Minimum panel width. Default: `10rem`.
 * @cssprop --uwc-spb-item-padding      - Dropdown item padding. Default: `0.5rem 1rem`.
 * @cssprop --uwc-spb-item-font-size    - Dropdown item font size. Default: font-size-md token.
 * @cssprop --uwc-spb-item-hover-bg     - Item hover/focus background.
 * @cssprop --uwc-spb-item-danger-color - Danger item text color. Default: danger token.
 * @cssprop --uwc-spb-z                 - Panel z-index. Default: `9999`.
 * @cssprop --uwc-spb-duration          - Panel open/close animation duration. Default: `120ms`.
 */
export class UwcSplitButton extends LitElement {
  static override styles: CSSResultGroup = [styles];

  // ── Properties ─────────────────────────────────────────────────────────────

  /** Label text on the primary action button. */
  @property() label?: string;

  /** Icon name rendered inside the primary button. */
  @property() icon?: string;

  /** Icon position on the primary button: `'left'` (default) or `'right'`. */
  @property({ attribute: 'icon-pos' }) iconPos: SplitButtonIconPos = 'left';

  /** Visual variant — matches all uwc-button variants. */
  @property({ reflect: true }) variant: SplitButtonVariant = 'primary';

  /** Size variant. */
  @property({ reflect: true }) size: SplitButtonSize = 'medium';

  /** Add an elevation shadow. */
  @property({ type: Boolean, reflect: true }) raised = false;

  /** Pill-shaped outer corner radius. */
  @property({ type: Boolean, reflect: true }) rounded = false;

  /** Border-only (no background fill). */
  @property({ type: Boolean, reflect: true }) outlined = false;

  /** Ghost / text-only style — no border or background. */
  @property({ type: Boolean, reflect: true }) text = false;

  /** Disable the entire component (both buttons). */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Show a spinner on the primary button and prevent clicks. */
  @property({ type: Boolean, reflect: true }) loading = false;

  /** Icon name used as the loading spinner. */
  @property({ attribute: 'loading-icon' }) loadingIcon = 'arrow-clockwise';

  /** Stretch the component to fill its container width. */
  @property({ type: Boolean, reflect: true }) fluid = false;

  /** Dropdown menu items. */
  @property({ type: Array }) items: MenuItem[] = [];

  /** Dropdown panel placement relative to the chevron button. */
  @property() placement: Placement = 'bottom-end';

  /** Gap in px between the chevron button and the panel. */
  @property({ type: Number }) offset = 4;

  /**
   * Native Invoker Command forwarded to the primary action button (e.g.
   * `show-modal`, `toggle-popover`). Inert where the platform lacks support.
   */
  @property() command?: string;

  /** Id of the element the primary button's `command` targets. */
  @property() commandfor?: string;

  // ── Internal ───────────────────────────────────────────────────────────────

  @query('.uwc-spb__trigger') private _triggerBtn!: HTMLButtonElement;
  @query('.uwc-spb__panel')  private _panelEl!: HTMLUListElement;

  @state() private _focusedIndex = -1;

  private readonly _onKeydown = this._handleKeydown.bind(this);

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panelEl,

    onTriggerFound: (el) => {
      const onClick = (e: Event): void => {
        e.stopPropagation();
        this._pc.toggle();
      };
      el.addEventListener('click', onClick);
      return () => el.removeEventListener('click', onClick);
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => {
      this._focusedIndex = -1;
      document.addEventListener('keydown', this._onKeydown);
      emit(this, 'uwc-show');
      requestAnimationFrame(() => this._focusItem(this._firstFocusableIndex()));
    },

    afterHide: () => {
      this._focusedIndex = -1;
      document.removeEventListener('keydown', this._onKeydown);
      emit(this, 'uwc-hide');
    },
  });

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override firstUpdated(): void {
    this._pc.firstUpdated(null, null);
    this._pc.setTriggerElement(this._triggerBtn);
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._pc.updated(changed as Map<PropertyKey, unknown>, null, null);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeydown);
  }

  // ── Item navigation ────────────────────────────────────────────────────────

  private _focusableItems(): { it: MenuItem; i: number }[] {
    return this.items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => !it.separator && it.visible !== false && !it.disabled);
  }

  private _firstFocusableIndex(): number {
    return this._focusableItems()[0]?.i ?? -1;
  }

  private _focusItem(idx: number): void {
    if (idx < 0) return;
    this._focusedIndex = idx;
    const el = this._panelEl?.querySelector<HTMLElement>(`[data-spb-index="${idx}"]`);
    el?.focus();
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────

  private _handleKeydown(e: KeyboardEvent): void {
    const focusable = this._focusableItems();
    const curPos    = focusable.findIndex(({ i }) => i === this._focusedIndex);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = focusable[(curPos + 1) % focusable.length];
        if (next) this._focusItem(next.i);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = focusable[(curPos - 1 + focusable.length) % focusable.length];
        if (prev) this._focusItem(prev.i);
        break;
      }
      case 'Home':
        e.preventDefault();
        if (focusable[0]) this._focusItem(focusable[0].i);
        break;
      case 'End': {
        e.preventDefault();
        const last = focusable.at(-1);
        if (last) this._focusItem(last.i);
        break;
      }
      case 'Tab':
        this._pc.hide();
        break;
    }
  }

  // ── Item execution ─────────────────────────────────────────────────────────

  private _executeItem(item: MenuItem, e: Event): void {
    if (item.disabled) return;
    const ev: MenuItemCommandEvent = { originalEvent: e, item };
    item.command?.(ev);
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', { item, originalEvent: e });
    this._pc.hide();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Open the dropdown panel programmatically. */
  show(): void   { this._pc.show(); }

  /** Close the dropdown panel programmatically. */
  hide(): void   { this._pc.hide(); }

  /** Toggle the dropdown panel. */
  toggle(): void { this._pc.toggle(); }

  /** Whether the dropdown panel is currently open. */
  get isOpen(): boolean { return this._pc.open; }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _iconSize(): string {
    // em-relative: the icon tracks the split button's per-size font-size (which
    // itself scales with the global Theme Designer scale), covering every size.
    return '1.1em';
  }

  private _spawnRipple(e: MouseEvent, selector: string): void {
    const btn = this.shadowRoot?.querySelector<HTMLElement>(selector);
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x    = e.clientX - rect.left - size / 2;
    const y    = e.clientY - rect.top  - size / 2;
    const span = document.createElement('span');
    span.className = 'uwc-spb-ripple';
    span.style.cssText = `width:${size}px;height:${size}px;left:${x}px;top:${y}px`;
    btn.appendChild(span);
    span.addEventListener('animationend', () => span.remove(), { once: true });
  }

  private _renderItem(item: MenuItem, idx: number): TemplateResult {
    if (item.visible === false) return html``;

    if (item.separator) {
      return html`<li class="uwc-spb__separator" role="separator" aria-hidden="true"></li>`;
    }

    const isFocused = this._focusedIndex === idx;

    const inner = html`
      ${item.icon
        ? html`<span class="uwc-spb__item-icon ${item.iconClass ?? ''}" aria-hidden="true">
            <uwc-icon name=${item.icon} size="1.05em"></uwc-icon>
          </span>`
        : nothing}
      <span class="uwc-spb__item-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span class="uwc-spb__item-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}
    `;

    return html`
      <li
        role="none"
        class=${classMap({
          'uwc-spb__item':           true,
          'uwc-spb__item--disabled': !!item.disabled,
          'uwc-spb__item--focused':  isFocused,
          'uwc-spb__item--danger':   !!item.danger,
        })}
        style=${item.style ? styleMap(item.style as Record<string, string>) : ''}
      >
        ${item.url && !item.disabled
          ? html`
            <a
              href=${item.url}
              target=${item.target ?? '_self'}
              role="menuitem"
              class="uwc-spb__item-inner"
              data-spb-index=${idx}
              tabindex=${isFocused ? '0' : '-1'}
              @click=${(e: MouseEvent) => { e.stopPropagation(); this._executeItem(item, e); }}
            >${inner}</a>`
          : html`
            <button
              type="button"
              role="menuitem"
              class="uwc-spb__item-inner"
              data-spb-index=${idx}
              tabindex=${isFocused ? '0' : '-1'}
              ?disabled=${item.disabled}
              @click=${(e: MouseEvent) => { e.stopPropagation(); this._executeItem(item, e); }}
              @focus=${() => { this._focusedIndex = idx; }}
            >${inner}</button>`}
      </li>`;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  protected override render(): TemplateResult {
    const showIcon  = (!!this.icon && !this.loading) || this.loading;
    const iconName  = this.loading ? this.loadingIcon : this.icon!;
    const iconSize  = this._iconSize();
    const isOpen    = this._pc.open;
    const visible   = this.items.filter(it => it.visible !== false);

    const iconEl = showIcon
      ? html`<uwc-icon class="uwc-spb__icon" name=${iconName} size=${iconSize} ?spin=${this.loading} aria-hidden="true"></uwc-icon>`
      : nothing;

    const labelEl = this.label
      ? html`<span class="uwc-spb__label">${this.label}</span>`
      : html`<slot></slot>`;

    return html`
      <div
        class=${classMap({
          'uwc-spb':          true,
          'uwc-spb--fluid':   this.fluid,
          'uwc-spb--disabled': this.disabled,
        })}
        role="group"
        aria-label=${this.label ?? 'Split button'}
      >
        <!-- Primary action button -->
        <button
          type="button"
          part="main-btn"
          class=${classMap({
            'uwc-spb__main':         true,
            'uwc-spb__main--loading': this.loading,
          })}
          command=${ifDefined(this.command)}
          commandfor=${ifDefined(this.commandfor)}
          ?disabled=${this.disabled || this.loading}
          aria-busy=${this.loading ? 'true' : 'false'}
          @click=${(e: MouseEvent) => {
            if (this.disabled || this.loading) return;
            this._spawnRipple(e, '.uwc-spb__main');
            emit(this, 'uwc-click', { originalEvent: e });
          }}
        >
          ${this.iconPos === 'right'
            ? html`${labelEl}${iconEl}`
            : html`${iconEl}${labelEl}`}
        </button>

        <!-- Chevron dropdown trigger -->
        <button
          type="button"
          part="trigger-btn"
          class="uwc-spb__trigger"
          aria-haspopup="menu"
          aria-expanded=${String(isOpen)}
          aria-label="More options"
          ?disabled=${this.disabled}
        >
          <uwc-icon
            name=${isOpen ? 'chevron-up' : 'chevron-down'}
            size=${iconSize}
            aria-hidden="true">
          </uwc-icon>
        </button>

        <!-- Dropdown panel -->
        <ul
          class="uwc-spb__panel"
          part="panel"
          popover="manual"
          role="menu"
          data-placement=${this.placement}
          aria-label=${this.label ? `${this.label} options` : 'Options'}
        >
          ${repeat(
            visible,
            (item) => item.id ?? item.label ?? item.separator,
            (item, i) => this._renderItem(item, i),
          )}
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-splitbutton': UwcSplitButton; }
}
